import { useMemo } from 'react';
import type { DatabaseMetaInfo, EnumLookupTarget, EnumValueCache } from '@/types/electron';
import {
  resolveEffectiveLookupDeclaration,
  resolveLookupPairsMode,
} from '../utils/effectiveLookupDeclaration';
import { buildLookupLabelMap } from '../utils/lookupLabelMap';
import type { TableExplorerHostLookupDeclaration } from '../../table-explorer/components/tableExplorerHostPorts';

/** Minimal grid-column shape this hook needs: the grid column id plus its
 *  source-table identity (as both `ResultsGrid` and `TableExplorerGrid`
 *  already carry on their normalized columns). */
export type LookupLabelMapColumn = {
  id: string;
  sourceSchema?: string;
  sourceTable?: string;
  sourceColumn?: string;
};

/**
 * One lookup column's cached pairs plus the provenance the cell tooltip
 * shows. Everything here comes from the MetaInfo snapshot — rendering a
 * label never costs a query.
 */
export type LookupLabelInfo = {
  /** key -> label. See `buildLookupLabelMap` for the absent-vs-null contract.
   *  MAY be empty for an implicit column whose pairs haven't been fetched yet
   *  — that empty map is what the self-heal treats as an all-miss column. */
  labels: Map<string, string | null>;
  /** The FK target this column reads its pairs from NOW — the live
   *  declaration / FK resolution, falling back to the cache's own stamp only
   *  when there is no live target left (see `lookupTargetOf`). */
  target: EnumLookupTarget | undefined;
  /** When the cached pairs were sampled (ISO). Absent when there is no cache. */
  sampledAt: string | undefined;
  /**
   * The cached pairs hit the 200-key cap — the target holds more rows than a
   * lookup cache can ever carry. `useLookupLabelSelfHeal` reads this to stop
   * treating a missing key as staleness: another capped refetch would return
   * the same 200 and leave the same keys missing.
   */
  truncated: boolean;
};

/**
 * Per-column label maps + provenance for every lookup enum visible in this
 * MetaInfo snapshot, keyed by the grid column id. Two kinds qualify, resolved
 * through the shared `resolveEffectiveLookupDeclaration` seam so they can
 * never drift:
 *
 *  - **explicit** — a column-level `source: 'lookup'` declaration (a
 *    declaration of any other source suppresses the column entirely, exactly
 *    as it suppresses implicitness);
 *  - **implicit** — no declaration at all, but `fkLookupTargets` resolves the
 *    column to a table flagged in `metaInfo.referenceTables`.
 *
 * The two differ in ONE place — when the resulting label map is empty:
 *
 *  - explicit → the column is OMITTED (unchanged behavior). An explicit
 *    declaration is warmed at declare time, so an empty map means the user
 *    declared and never fetched; churning a background fetch for it is not
 *    this hook's job.
 *  - implicit → the column is still EMITTED, with an empty map. There is no
 *    declare-time warm-up for an implicit column (nobody declared anything on
 *    it), so this entry is the only thing that can ever seed its cache: every
 *    visible key misses an empty map, and `useLookupLabelSelfHeal` turns that
 *    into exactly one throttled pair fetch. `withLookupLabel` renders nothing
 *    for an empty map, so the seed entry is invisible until it heals.
 *
 * JSON-path declarations and path-scoped cache entries are excluded from both
 * kinds — grid labels are column-level only.
 *
 * Schema comparison is `??`-normalized (never truthiness) so a Kusto `''`
 * schema matches a column with no `sourceSchema`.
 *
 * Memoized off `metaInfo` + `columns` + `fkLookupTargets` only.
 */
export function useLookupLabelMaps(args: {
  metaInfo: DatabaseMetaInfo | null | undefined;
  columns: readonly LookupLabelMapColumn[];
  /**
   * Per-column resolved FK target (`resolveLookupEnumTarget`), as both grids
   * memoize it. Required for implicit lookup enums; omit it and only explicit
   * declarations resolve.
   */
  fkLookupTargets?: Map<string, EnumLookupTarget>;
  hostLookupDeclarations?: Map<string, TableExplorerHostLookupDeclaration>;
}): Map<string, LookupLabelInfo> {
  const { metaInfo, columns, fkLookupTargets, hostLookupDeclarations } = args;

  return useMemo(() => {
    const result = new Map<string, LookupLabelInfo>();
    if (!metaInfo && !hostLookupDeclarations?.size) return result;

    for (const column of columns) {
      if (!column.sourceTable || !column.sourceColumn) continue;
      const schema = column.sourceSchema ?? '';

      const effective = resolveEffectiveLookupDeclaration({
        metaInfo,
        schema,
        table: column.sourceTable,
        column: column.sourceColumn,
        resolvedTarget: fkLookupTargets?.get(column.id),
        hostDeclaration: hostLookupDeclarations?.get(column.id),
      });
      if (!effective) continue;
      // A declaration of any other source is a deliberate user choice — it
      // owns the column, and its values are not key→label pairs.
      if (effective.kind === 'explicit' && effective.declaration.source !== 'lookup') continue;
      // This hook IS the cached pipeline: its entries are what
      // `useLookupLabelSelfHeal` turns into whole-set pair fetches written
      // through to MetaInfo. An on-demand target must never be read that way —
      // `useOnDemandLookupLabels` owns those columns end to end (implicit AND
      // explicit), so they leave here entirely.
      if (resolveLookupPairsMode(metaInfo, effective) === 'on-demand') continue;

      const cache = metaInfo?.enumValueCache.find(
        (c) =>
          !c.path &&
          (c.schema ?? '') === schema &&
          c.table === column.sourceTable &&
          c.column === column.sourceColumn,
      );

      // A host lookup (Azure Table, S4 review D, D4) names the constraint its pairs must come from
      // (`pairsScope`, e.g. PartitionKey = 'us'); pairs cached under another one — or before the constraint was
      // honoured — are not shown, and the column is emitted empty so the miss self-heal refetches it. SQL grids
      // pass no host declarations, so nothing changes for them.
      const pairsScope = hostLookupDeclarations?.get(column.id)?.pairsScope;
      const staleForHost = pairsScope !== undefined && cache?.lookup?.scope !== pairsScope;
      const labels = staleForHost ? new Map<string, string | null>() : buildLookupLabelMap(cache);
      // Explicit columns keep the omit-when-empty behavior; implicit columns
      // emit the empty seed entry (see the doc comment above).
      if (labels.size === 0 && effective.kind === 'explicit' && !staleForHost) continue;

      result.set(column.id, {
        labels,
        target: lookupTargetOf(cache, effective),
        sampledAt: cache?.sampledAt,
        truncated: cache?.truncated ?? false,
      });
    }

    return result;
  }, [metaInfo, columns, fkLookupTargets, hostLookupDeclarations]);
}

/**
 * Which target this column reads its pairs from **now**.
 *
 * The LIVE resolution wins — the explicit `declaration.lookup`, or the
 * implicit FK target `resolveEffectiveLookupDeclaration` just resolved. It has
 * to: this value is what `useLookupLabelSelfHeal` refetches from, and the IPC
 * re-stamps the cache with whatever it fetched. Preferring the cache's own
 * `lookup` stamp made that loop self-perpetuating — re-point a loose FK at a
 * different table and the column stayed pinned to the OLD one forever, because
 * every refresh re-read the stale target and re-wrote the stale stamp.
 *
 * The cache stamp is the FALLBACK, for the one case with no live target at
 * all: a cache orphaned from its declaration's target (a pre-stamp
 * declaration, or one whose `lookup` was cleared). There it is pure
 * provenance display — nothing refetches from it, because a column with no
 * live target has nothing to refetch.
 */
function lookupTargetOf(
  cache: EnumValueCache | undefined,
  effective:
    | { kind: 'explicit'; declaration: { lookup?: EnumLookupTarget } }
    | { kind: 'implicit' | 'host'; lookup: EnumLookupTarget },
): EnumLookupTarget | undefined {
  const live = effective.kind === 'explicit' ? effective.declaration.lookup : effective.lookup;
  return live ?? cache?.lookup;
}
